import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Box, Sparkles } from 'lucide-react';
import {
  CENTERPIECE_STYLES,
  CENTERPIECE_STYLE_LABELS,
  CHAIR_STYLES,
  CHAIR_STYLE_LABELS,
  CUTLERY_FINISHES,
  CUTLERY_FINISH_LABELS,
  DECOR_FLOOR_FINISHES,
  DECOR_FLOOR_FINISH_LABELS,
  DECOR_PICTURE_LABELS,
  GLASS_STYLES,
  GLASS_STYLE_LABELS,
  DECOR_LIBRARY_CATEGORIES,
  LINEN_PRESETS,
  readDecorLook,
  expandLook,
  resolveDecor,
  type DecorField,
  type DecorPicks,
  type DecorLook,
  type DecorPalette,
  type DecorPictureKind,
  type DecorSource,
  type EventDecor,
  type ResolvedDecorValues,
} from '@peles/shared';
import { EntityModal } from '../../common';
import ColorField, { type ColorSwatch } from '../../common/ColorField';
import { SegmentedControl } from '../../common/SegmentedControl';
import { useDialog } from '../../../context/DialogContext';
import {
  useEventDecor,
  useSaveEventDecor,
  useSaveFloorPlanDecor,
  useSuggestDecor,
} from '../../../hooks/useDecor';
import { swatchesOf, withGroupPalette } from '../../../utils/imagePalette';
import { DecorChoice } from './DecorChoice';
import { DecorFieldsEditor } from '../../decor-library/DecorFieldsEditor';
import { DecorPicksList } from '../../decor-library/DecorPicksList';
import { DecorPictureGroup } from './DecorPictureGroup';
import { FlowerColorsField } from './FlowerColorsField';
import { useDecorPictures } from './useDecorPictures';

type Scope = 'event' | 'plan';

export interface DecorLookModalProps {
  open: boolean;
  onClose: () => void;
  eventId: string;
  /** The plan on screen — offered as "This plan only", for its own changes. */
  plan?: { id: string; name: string; decor?: DecorLook | null } | null;
  /** Shows the look as it stands, unsaved changes and all, in the 3D view. */
  onPreview?: (decor: ResolvedDecorValues) => void;
}

const opts = <T extends string>(list: readonly T[], labels: Record<T, string>) =>
  list.map((value) => ({ value, label: labels[value] }));

function sameLook(a: DecorLook, b: DecorLook): boolean {
  return JSON.stringify(readDecorLook(a)) === JSON.stringify(readDecorLook(b));
}

function without(look: DecorLook, field: DecorField): DecorLook {
  const next = { ...look };
  delete next[field];
  return next;
}

/**
 * The look of the event's room — linen, chairs, flowers, tableware, walls
 * and floor — set beside the pictures it is taken from, for the whole event or
 * for the plan on screen only. The 3D view, the AI Render Studio and the
 * client's 3D all draw it.
 *
 * Pictures are saved the moment they are added or taken out (they are files),
 * and so are the colours read from them; the choices are a draft until Save.
 */
export function DecorLookModal({ open, onClose, eventId, plan, onPreview }: DecorLookModalProps) {
  const { confirm } = useDialog();
  const decor = useEventDecor(open ? eventId : null);
  const saveEvent = useSaveEventDecor(eventId);
  const savePlan = useSaveFloorPlanDecor(plan?.id ?? '');
  const suggest = useSuggestDecor(eventId);

  const savedEvent = useMemo(() => decor.data?.look ?? {}, [decor.data]);
  const savedPlan = useMemo(() => readDecorLook(plan?.decor ?? {}), [plan?.decor]);
  const palette = decor.data?.palette ?? {};

  const [scope, setScope] = useState<Scope>('event');
  const [eventLook, setEventLook] = useState<DecorLook>({});
  const [planLook, setPlanLook] = useState<DecorLook>({});
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const loadedFor = useRef<string | null>(null);
  // Whether the draft holds what is stored. Until then it is empty, which
  // would read as "every saved choice removed" — so nothing is dirty and
  // nothing is saved while the look loads or after it failed to.
  const [ready, setReady] = useState(false);

  // The draft starts from what is stored, once each time the panel opens.
  useEffect(() => {
    if (!open) {
      loadedFor.current = null;
      setReady(false);
      return;
    }
    if (!decor.data || loadedFor.current === eventId) return;
    loadedFor.current = eventId;
    setEventLook(decor.data.look);
    setPlanLook(savedPlan);
    setScope('event');
    setNote(null);
    setError(null);
    setReady(true);
  }, [open, decor.data, eventId, savedPlan]);

  // Every write of the event's decor goes through one queue — the colours
  // read from the pictures (saved at once, beside the stored look, never the
  // draft) and Save (the draft's look, beside the stored palette). Each builds
  // on the answer of the write before it, so neither ever puts back a look or
  // a palette another write has just replaced.
  const writes = useRef<Promise<void> | null>(null);
  const latest = useRef(decor.data);
  if (!writes.current) latest.current = decor.data;
  const writeEvent = (build: (stored: EventDecor) => EventDecor): Promise<void> => {
    const run = (writes.current ?? Promise.resolve()).then(async () => {
      latest.current = await saveEvent.mutateAsync(
        build(latest.current ?? { look: {}, palette: {} }),
      );
    });
    const tail = run.catch(() => {});
    writes.current = tail;
    void tail.then(() => {
      if (writes.current === tail) writes.current = null;
    });
    return run;
  };
  const pictures = useDecorPictures(eventId, (kind, group) =>
    writeEvent((stored) => ({
      look: stored.look,
      palette: withGroupPalette(stored.palette, kind, group),
    })),
  );

  // Pictures whose colours were never read (a read that failed, pictures
  // added elsewhere) are read once when the panel opens.
  const repainted = useRef(false);
  useEffect(() => {
    if (!open) {
      repainted.current = false;
      return;
    }
    if (repainted.current || !decor.data || pictures.loading) return;
    repainted.current = true;
    const stored = decor.data.palette;
    (['chairs', 'flowers', 'tableware', 'hall'] as const)
      .filter(
        (kind) =>
          pictures.byKind[kind].length > 0 &&
          (kind === 'hall' ? !stored.hallWall && !stored.hallFloor : !stored[kind]?.length),
      )
      .forEach((kind) => void pictures.refresh(kind));
  }, [open, decor.data, pictures]);

  const dirty =
    ready && (!sameLook(eventLook, savedEvent) || (!!plan && !sameLook(planLook, savedPlan)));

  // What a field shows when nothing is chosen at this level, and where it comes from.
  const below = useMemo(
    () =>
      scope === 'event'
        ? resolveDecor({ look: {}, palette })
        : resolveDecor({ look: eventLook, palette }),
    [scope, eventLook, palette],
  );
  // What the fields of the level being set show — the plan's changes only while setting the plan.
  const scopeResolved = useMemo(
    () => resolveDecor({ look: eventLook, palette }, scope === 'plan' ? planLook : null).values,
    [eventLook, planLook, palette, scope],
  );
  const draftResolved = useMemo(
    () => resolveDecor({ look: eventLook, palette }, plan ? planLook : null).values,
    [eventLook, planLook, palette, plan],
  );

  const look = scope === 'event' ? eventLook : planLook;
  const setLook = scope === 'event' ? setEventLook : setPlanLook;
  // A library piece picked at this level decides the fields it covers, so a
  // field left alone shows the piece's value — as DecorFieldsEditor does.
  const fromPicks = useMemo(
    () => expandLook(look.picks ? { picks: look.picks } : null) as Record<string, unknown>,
    [look.picks],
  );
  const picked = (field: DecorField) => fromPicks[field] !== undefined;
  const underValue = (field: DecorField): unknown =>
    picked(field) ? fromPicks[field] : below.values[field];
  const set = <K extends DecorField>(field: K, value: DecorLook[K] | undefined) =>
    setLook((current) =>
      value === undefined ? without(current, field) : { ...current, [field]: value },
    );

  const sourceLabel = (source: DecorSource): string =>
    scope === 'plan'
      ? 'The event’s'
      : source === 'pictures'
        ? 'From your pictures'
        : source === 'event'
          ? 'The event’s'
          : 'Default';
  const inheritedText = (field: DecorField) =>
    picked(field) ? 'Chosen piece’s' : `${sourceLabel(below.sources[field])}`;
  const inheritedPrefix = (field: DecorField) =>
    picked(field) ? 'Chosen piece’s' : scope === 'plan' ? 'Event’s' : 'Auto';
  const choiceLabel = <T extends string>(field: DecorField, labels: Record<T, string>) =>
    `${inheritedPrefix(field)}: ${labels[underValue(field) as T]}`;

  const inheritedWord = scope === 'plan' ? 'Event’s' : 'Auto';
  const extraFields = (fields: DecorField[], extra = swatches) => (
    <DecorFieldsEditor
      fields={fields}
      look={look}
      onChange={setLook}
      inherited={below.values as unknown as Record<string, unknown>}
      inheritedLabel={inheritedWord}
      swatches={extra}
    />
  );

  const swatches: ColorSwatch[] = swatchesOf(palette).map((color) => ({ color }));
  const linenSwatches: ColorSwatch[] = [
    ...swatches,
    ...LINEN_PRESETS.filter((p) => !swatches.some((s) => s.color === p.color)),
  ];

  const color = (field: DecorField, label: string, extra = swatches) => (
    <ColorField
      label={label}
      value={look[field] as string | undefined}
      onChange={(v) => set(field, v)}
      inheritedValue={underValue(field) as string | null}
      inheritedLabel={inheritedText(field)}
      swatches={extra}
    />
  );

  const readPictures = async () => {
    setError(null);
    setNote(null);
    try {
      const { look: found, pictures: read } = await suggest.mutateAsync();
      const count = Object.keys(found).length;
      if (count === 0) {
        setNote(`The AI read ${read} picture${read === 1 ? '' : 's'} but could not tell the look.`);
        return;
      }
      setScope('event');
      setEventLook((current) => ({ ...current, ...found }));
      setNote(
        `Filled in ${count} thing${count === 1 ? '' : 's'} from ${read} picture${read === 1 ? '' : 's'}. Check them, then Save.`,
      );
    } catch (err) {
      const message = (err as { response?: { data?: { error?: { message?: string } } } })?.response
        ?.data?.error?.message;
      setError(message ?? 'The AI could not read the pictures just now. Try again in a moment.');
    }
  };

  const save = async () => {
    if (!ready) return;
    setError(null);
    try {
      if (!sameLook(eventLook, savedEvent)) {
        const next = readDecorLook(eventLook);
        await writeEvent((stored) => ({ look: next, palette: stored.palette }));
      }
      if (plan && !sameLook(planLook, savedPlan)) {
        const clean = readDecorLook(planLook);
        await savePlan.mutateAsync(Object.keys(clean).length ? clean : null);
      }
      onClose();
    } catch {
      setError('The look could not be saved. Try again.');
    }
  };

  const close = async () => {
    if (
      dirty &&
      !(await confirm({
        title: 'Discard your changes?',
        message: 'The look has changes that are not saved.',
        confirmLabel: 'Discard',
        variant: 'danger',
      }))
    ) {
      return;
    }
    onClose();
  };

  const saving = saveEvent.isPending || savePlan.isPending;
  // Save waits for the pictures: their colours are still being written.
  const pictureWork = pictures.working;

  const section = (kind: DecorPictureKind, title: string, fields: ReactNode) => (
    <section
      className="rounded-xl border border-gray-200 bg-white p-4"
      aria-label={title}
      data-testid={`decor-section-${kind}`}
    >
      <h3 className="mb-3 text-base font-semibold text-gray-900">{title}</h3>
      <div className="grid gap-5 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
        <DecorPictureGroup
          kind={kind}
          pictures={pictures.byKind[kind]}
          busy={pictures.isBusy(kind)}
          onAdd={(files) => void pictures.add(kind, files)}
          onRemove={(p) => void pictures.drop(kind, p)}
          colors={
            kind === 'hall'
              ? [palette.hallWall, palette.hallFloor].filter((c): c is string => !!c)
              : ((palette as DecorPalette)[kind] ?? [])
          }
        />
        <div className="space-y-4">{fields}</div>
      </div>
    </section>
  );

  return (
    <EntityModal
      open={open}
      onClose={() => void close()}
      title="The look"
      size="xl"
      footer={
        <div className="flex flex-wrap items-center justify-end gap-2">
          {onPreview && (
            <button
              type="button"
              onClick={() => onPreview(draftResolved)}
              className="mr-auto inline-flex items-center gap-1.5 min-h-11 sm:min-h-9 rounded-lg border border-gray-300 px-3 text-sm text-gray-700 hover:bg-gray-50"
            >
              <Box className="h-4 w-4" />
              Preview in 3D
            </button>
          )}
          <button
            type="button"
            onClick={() => void close()}
            className="min-h-11 sm:min-h-9 rounded-lg border border-gray-300 px-4 text-sm text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void save()}
            disabled={!dirty || saving || pictureWork}
            title={pictureWork ? 'Waiting for the pictures to finish' : undefined}
            className="min-h-11 sm:min-h-9 rounded-lg bg-primary-600 px-4 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      }
    >
      {decor.isLoading ? (
        <div className="space-y-3" data-testid="decor-loading">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-40 animate-pulse rounded-xl bg-gray-100" />
          ))}
        </div>
      ) : decor.isError ? (
        <p role="alert" className="text-sm text-red-600">
          The look could not be loaded. Close this and try again.
        </p>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Add pictures of what the client wants. Their colours are used straight away; choose the
            styles below, or let the AI read them from the pictures. The 3D view, the AI render and
            the client’s 3D all use this look.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            {plan && (
              <SegmentedControl<Scope>
                ariaLabel="Set the look for"
                value={scope}
                onChange={setScope}
                options={[
                  { id: 'event', label: 'Whole event' },
                  { id: 'plan', label: `Only ${plan.name}` },
                ]}
              />
            )}
            <button
              type="button"
              onClick={() => void readPictures()}
              disabled={pictures.total === 0 || suggest.isPending}
              title={pictures.total === 0 ? 'Add some pictures first' : undefined}
              className="inline-flex items-center gap-1.5 min-h-11 sm:min-h-9 rounded-lg border border-primary-200 bg-primary-50 px-3 text-sm font-medium text-primary-700 hover:bg-primary-100 disabled:opacity-50"
            >
              <Sparkles className="h-4 w-4" />
              {suggest.isPending ? 'Reading the pictures…' : 'Read the pictures with AI'}
            </button>
          </div>
          {scope === 'plan' && (
            <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
              Changes here apply to {plan?.name} only. Anything left on “Event’s” follows the whole
              event’s look.
            </p>
          )}
          {note && (
            <p role="status" className="rounded-lg bg-green-50 px-3 py-2 text-sm text-green-800">
              {note}
            </p>
          )}
          {(error || pictures.error) && (
            <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
              {error ?? pictures.error}
            </p>
          )}
          {pictures.unread.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
              <span>
                The colours of{' '}
                {pictures.unread.map((k) => DECOR_PICTURE_LABELS[k].toLowerCase()).join(', ')} could
                not be updated just now, so they stay as they were.
              </span>
              <button
                type="button"
                onClick={() => pictures.unread.forEach((k) => void pictures.refresh(k))}
                disabled={pictures.unread.some((k) => pictures.isBusy(k))}
                className="min-h-11 sm:min-h-9 rounded-md border border-amber-300 bg-white px-3 text-amber-800 hover:bg-amber-100 disabled:opacity-50"
              >
                Read them again
              </button>
            </div>
          )}

          <section
            className="rounded-xl border border-gray-200 bg-white p-4"
            aria-label="From your decor library"
            data-testid="decor-section-library"
          >
            <h3 className="text-base font-semibold text-gray-900">From your decor library</h3>
            <p className="mb-3 text-sm text-gray-600">
              Choose the pieces you will use — every table, bar and sofa on{' '}
              {scope === 'plan' ? plan?.name : 'the event’s plans'} takes them, and any one of them
              can still choose its own on the floor plan. The AI render is shown their pictures.
            </p>
            <DecorPicksList
              categories={DECOR_LIBRARY_CATEGORIES}
              look={look}
              onChange={setLook}
              inheritedPicks={scope === 'plan' ? ((eventLook.picks ?? {}) as DecorPicks) : {}}
              inheritedLabel="Event’s"
              forWhat={scope === 'plan' ? (plan?.name ?? 'this plan') : 'the whole event'}
            />
          </section>

          {section(
            'chairs',
            DECOR_PICTURE_LABELS.chairs,
            <>
              <DecorChoice
                label="Chair style"
                options={opts(CHAIR_STYLES, CHAIR_STYLE_LABELS)}
                value={look.chairStyle}
                onChange={(v) => set('chairStyle', v)}
                inheritedLabel={choiceLabel('chairStyle', CHAIR_STYLE_LABELS)}
              />
              {color('chairColor', 'Chair colour')}
              {color('cushionColor', 'Cushion colour')}
            </>,
          )}

          {section(
            'flowers',
            DECOR_PICTURE_LABELS.flowers,
            <>
              <DecorChoice
                label="On each table"
                options={opts(CENTERPIECE_STYLES, CENTERPIECE_STYLE_LABELS)}
                value={look.centerpiece}
                onChange={(v) => set('centerpiece', v)}
                inheritedLabel={choiceLabel('centerpiece', CENTERPIECE_STYLE_LABELS)}
              />
              <FlowerColorsField
                value={look.flowerColors}
                inherited={underValue('flowerColors') as string[]}
                inheritedLabel={inheritedText('flowerColors')}
                onChange={(v) => set('flowerColors', v)}
                swatches={swatches}
              />
              {color('greeneryColor', 'Greenery')}
            </>,
          )}

          {section(
            'tableware',
            'Linen & place settings',
            <>
              {color('linenColor', 'Linen', linenSwatches)}
              {extraFields(['linenFinish'])}
              {color('napkinColor', 'Napkins', linenSwatches)}
              {extraFields(['napkinFold'])}
              <DecorChoice
                label="Charger plates"
                options={[
                  { value: 'yes', label: 'Yes' },
                  { value: 'no', label: 'No' },
                ]}
                value={look.charger === undefined ? undefined : look.charger ? 'yes' : 'no'}
                onChange={(v) => set('charger', v === undefined ? undefined : v === 'yes')}
                inheritedLabel={`${inheritedPrefix('charger')}: ${underValue('charger') ? 'Yes' : 'No'}`}
              />
              {scopeResolved.charger && color('chargerColor', 'Charger colour')}
              {color('plateColor', 'Plates')}
              <DecorChoice
                label="Cutlery"
                options={opts(CUTLERY_FINISHES, CUTLERY_FINISH_LABELS)}
                value={look.cutlery}
                onChange={(v) => set('cutlery', v)}
                inheritedLabel={choiceLabel('cutlery', CUTLERY_FINISH_LABELS)}
              />
              <DecorChoice
                label="Glasses"
                options={opts(GLASS_STYLES, GLASS_STYLE_LABELS)}
                value={look.glass}
                onChange={(v) => set('glass', v)}
                inheritedLabel={choiceLabel('glass', GLASS_STYLE_LABELS)}
              />
              {scopeResolved.glass === 'tinted' && color('glassColor', 'Glass colour')}
              {extraFields(['glassShape', 'candleStyle', 'candleFinish'])}
            </>,
          )}

          {section(
            'hall',
            DECOR_PICTURE_LABELS.hall,
            <>
              {color('wallColor', 'Walls')}
              <DecorChoice
                label="Floor"
                options={opts(DECOR_FLOOR_FINISHES, DECOR_FLOOR_FINISH_LABELS)}
                value={look.floorFinish}
                onChange={(v) => set('floorFinish', v)}
                inheritedLabel={choiceLabel('floorFinish', DECOR_FLOOR_FINISH_LABELS)}
              />
              {color('floorColor', 'Floor colour')}
            </>,
          )}

          <section
            className="rounded-xl border border-gray-200 bg-white p-4"
            aria-label="Bars, dance floor, chuppah & lounge"
            data-testid="decor-section-objects"
          >
            <h3 className="mb-3 text-base font-semibold text-gray-900">
              Bars, dance floor, chuppah & lounge
            </h3>
            <div className="grid gap-5 lg:grid-cols-2">
              {extraFields(
                ['cocktailStyle', 'cocktailColor', 'barStyle', 'barColor'],
                linenSwatches,
              )}
              {extraFields([
                'danceFloorStyle',
                'chuppahStyle',
                'chuppahFabricColor',
                'chuppahFlowerColors',
                'loungeStyle',
                'loungeColor',
              ])}
            </div>
          </section>
        </div>
      )}
    </EntityModal>
  );
}
